import { Button } from "@/components/ui/baseUi/button"
import { Text } from "@/components/ui/baseUi/text"
import { CoachOfferRemoteCard } from "@/modules/coachOffers/components/coachOfferRemoteCard"
import { useCoachOfferSidebar } from "@/modules/coachOffers/hooks/useCoachOfferSidebar"
import { ALL_OFFER_FILTER, REMOTE_OFFER_TYPES, type OfferTypeFilter } from "@/modules/coachOffers/page/coachOffersPage"
import { word } from "@/modules/core/utils/i18n"
import { cn } from "@/modules/lib/utils"
import { usePrograms } from "@/modules/programs/hooks/usePrograms"
import { useProgramSidebar } from "@/modules/programs/hooks/useProgramSidebar"
import { ProgramFilter } from "@/modules/programs/types/programTypes"
import { useQueryParam } from "@/modules/utils/navigation/useQueryParam"
import { PlusIcon } from "lucide-react"
import { useMemo } from "react"

type CoachOfferRemoteListProps = { selectedFilter: OfferTypeFilter } & React.ComponentProps<"div">

/*
 * In this view, we display remote offers that correspond mainly to programs.
 * The offer creation process is different from accompanied offers, as it is more program-centric.
 */
export function CoachOfferRemoteList({ selectedFilter, ...props }: CoachOfferRemoteListProps) {
  const { navigateToRemoteOfferTypeSelection } = useCoachOfferSidebar()
  const { navigateToProgramView } = useProgramSidebar()
  const {
    data: { content: userPrograms },
  } = usePrograms({ programFilter: ProgramFilter.OWNED })
  const [search] = useQueryParam("search")

  const filteredPrograms = useMemo(() => {
    const typeFiltered =
      selectedFilter === ALL_OFFER_FILTER
        ? userPrograms.filter((o) => REMOTE_OFFER_TYPES.includes(o.type))
        : userPrograms.filter((o) => o.type === selectedFilter)
    if (!search) return typeFiltered

    const lowerSearch = search.toLowerCase()
    return typeFiltered.filter(
      (o) => o.name.toLowerCase().includes(lowerSearch) || o.description.toLowerCase().includes(lowerSearch),
    )
  }, [userPrograms, selectedFilter, search])

  return (
    <div className={cn("flex flex-wrap gap-3", props.className)} {...props}>
      <div className="flex min-h-40 w-full flex-col justify-between gap-4 rounded-xl border border-dashed border-primary-300 p-4 lg:w-[49%]">
        <Text fontStyle="TM" className="text-textOnSurface-default">
          {word("coach.offers.add.remote.title")}
        </Text>
        <Button
          onClick={() => {
            void navigateToRemoteOfferTypeSelection()
          }}
          variant="primary"
          className="w-fit"
          size="lg"
        >
          <PlusIcon size={16} />
          {word("global.new")}
        </Button>
      </div>

      {filteredPrograms.map((program) => (
        <CoachOfferRemoteCard
          key={program.id}
          program={program}
          className="min-h-40 lg:w-[49%]"
          onClick={() => {
            navigateToProgramView(program.id, true)
          }}
        />
      ))}
    </div>
  )
}
